/**
 * ChiefOfStaff Chat Component
 * Main interface for interacting with the AI orchestrator
 */

import { useState, useEffect, useRef } from 'react';
import { Send, Loader2, Sparkles, X, Minimize2, Maximize2 } from 'lucide-react';
import { orchestratorAPI, type ChatMessage } from '../lib/orchestratorAPI';

interface ChiefOfStaffChatProps {
  onClose?: () => void;
  initialMessage?: string;
  fullScreen?: boolean;
}

export function ChiefOfStaffChat({ onClose, initialMessage, fullScreen = false }: ChiefOfStaffChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(fullScreen);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadHistory();
    if (initialMessage) {
      handleSendMessage(initialMessage);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadHistory = async () => {
    try {
      const history = await orchestratorAPI.getConversationHistory(20);
      setMessages(history);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const { response, chatMessage } = await orchestratorAPI.sendMessage(text);
      setMessages(prev => [...prev, chatMessage]);

      // Show next steps if available
      if (response.nextSteps && response.nextSteps.length > 0) {
        const nextStepsMessage: ChatMessage = {
          id: `nextsteps-${Date.now()}`,
          role: 'system',
          content: `**Next Steps:**\n${response.nextSteps.map((step, i) => `${i + 1}. ${step}`).join('\n')}`,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, nextStepsMessage]);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (message: string) => {
    handleSendMessage(message);
  };

  const clearHistory = async () => {
    await orchestratorAPI.clearHistory();
    setMessages([]);
  };

  const quickActions = orchestratorAPI.getQuickActions();

  return (
    <div className={`flex flex-col bg-white rounded-lg shadow-2xl ${isExpanded ? 'fixed inset-4 z-50' : 'h-[600px] w-full max-w-2xl'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">ChiefOfStaff AI</h3>
            <p className="text-xs text-purple-100">Your intelligent career assistant</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            {isExpanded ? (
              <Minimize2 className="w-5 h-5 text-white" />
            ) : (
              <Maximize2 className="w-5 h-5 text-white" />
            )}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Sparkles className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <h4 className="text-xl font-semibold text-gray-900 mb-2">Welcome to ChiefOfStaff AI</h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              I'm your intelligent career assistant. I can help you find jobs, create resumes,
              prepare for interviews, and much more!
            </p>
            <div className="grid grid-cols-2 gap-2 max-w-lg mx-auto">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(action.message)}
                  className="flex items-center space-x-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all text-left"
                >
                  <span className="text-2xl">{action.icon}</span>
                  <span className="text-sm font-medium text-gray-700">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-4 ${
                message.role === 'user'
                  ? 'bg-purple-600 text-white'
                  : message.role === 'system'
                  ? 'bg-blue-50 text-blue-900 border border-blue-200'
                  : 'bg-white text-gray-900 shadow-sm border border-gray-200'
              }`}
            >
              <div className="whitespace-pre-wrap">{message.content}</div>
              {message.metadata && (
                <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                  {message.metadata.agentsInvoked && message.metadata.agentsInvoked.length > 0 && (
                    <div>
                      Agents: {message.metadata.agentsInvoked.join(', ')}
                    </div>
                  )}
                  {message.metadata.executionTime && (
                    <div>
                      Processed in {Math.round(message.metadata.executionTime)}ms
                    </div>
                  )}
                  {message.metadata.confidence && (
                    <div>
                      Confidence: {Math.round(message.metadata.confidence * 100)}%
                    </div>
                  )}
                </div>
              )}
              <div className="text-xs opacity-70 mt-2">
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
              <div className="flex items-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                <span className="text-gray-600">ChiefOfStaff is thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        {messages.length > 0 && (
          <div className="mb-2 flex justify-end">
            <button
              onClick={clearHistory}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear history
            </button>
          </div>
        )}
        <div className="flex space-x-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about your career..."
            className="flex-1 resize-none rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || isLoading}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
